/**
 * IP / CIDR validation helpers for the web package.
 *
 * These wrap the canonical validators exported from `@sbclite/core`
 * (`validateIPv4`, `validateCIDR`) so the web UI does not duplicate the parsing
 * logic. The textarea-flavoured helpers below operate on multi-entry blobs
 * (one IP/CIDR per line or comma).
 */

import { validateIPv4 } from '@sbclite/core/util/sip';
import { validateCIDR } from '@sbclite/core/util/network';

/** True when `value` is a bare IPv4 address (e.g. `192.168.1.1`). */
export function isValidIpv4(value: string): boolean {
  return validateIPv4(value.trim());
}

/** True when `value` is an IPv4 CIDR range (e.g. `192.168.1.0/24`). */
export function isValidIpv4Cidr(value: string): boolean {
  return validateCIDR(value.trim());
}

/** True when `value` is either a bare IPv4 or an IPv4 CIDR range. */
export function isValidIpv4OrCidr(value: string): boolean {
  const entry = value.trim();
  if (entry === '') return false;
  return entry.includes('/') ? isValidIpv4Cidr(entry) : isValidIpv4(entry);
}

/**
 * Parse a textarea-style blob of IP/CIDR entries into a cleaned `string[]`.
 * Entries may be separated by newlines or commas.
 */
export function parseIpRangeEntries(raw: string): string[] {
  return raw
    .split(/[\n,]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
}

/** Return the entries from `raw` that are neither a valid IPv4 nor CIDR. */
export function findInvalidIpRangeEntries(raw: string): string[] {
  return parseIpRangeEntries(raw).filter((entry) => !isValidIpv4OrCidr(entry));
}

/** True when every entry in `raw` is a valid IPv4 or IPv4 CIDR (empty = valid). */
export function isValidIpRangeList(raw: string): boolean {
  return findInvalidIpRangeEntries(raw).length === 0;
}
